/**
 * Tile Card - Square compact card with glassmorphism effect
 * Created by neo on 21.01.26
 */
import * as React from 'react';
import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import { useNavigate } from 'react-router-dom';
import { ExploreEntry } from '../../Model/Explore/ExploreEntry';
import { Recipe } from '../../Model/Diet/Recipe/Recipe';
import { useEntryText } from './useEntryText';
import { getContentTypeLabel, getContentTypeColor } from './contentTypeUtils';

const CardContainer = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 1;
  border-radius: 20px;
  overflow: hidden;
  isolation: isolate;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    transform: translateY(-6px) rotate(-1deg);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.15);

    .image {
      transform: scale(1.1);
    }

    .glass-overlay {
      background: rgba(0, 0, 0, 0.1);
    }

    .type-badge {
      transform: scale(1.05);
    }
  }
`;

const BackgroundImage = styled.div<{ image?: string }>`
  position: absolute;
  inset: -1px;
  background: ${({ image }) =>
    image ? `url('${image}')` : 'linear-gradient(135deg, #00dda2 0%, #007763 100%)'};
  background-size: cover;
  background-position: center;
  transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  will-change: transform;
`;

const GlassOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.25);
  transition: all 0.35s ease;
`;

const Content = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 16px;
  color: white;
`;

const TypeBadge = styled.div<{ bgColor?: string }>`
  position: absolute;
  top: 12px;
  right: 12px;
  padding: 4px 10px;
  background: ${({ bgColor }) => bgColor || 'rgba(0, 221, 163, 0.9)'};
  backdrop-filter: blur(8px);
  border-radius: 12px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: white;
  transition: transform 0.3s ease;
`;

const Title = styled.h4`
  font-size: 1rem;
  font-weight: 700;
  margin: 0;
  color: white;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
`;


export type TileCardProps = {
  entry: ExploreEntry | Recipe;
  linkPrefix?: string;
};

export const TileCard: React.FC<TileCardProps> = observer(({ entry, linkPrefix = '/academy/' }) => {
  const { title, type, imageMedium } = useEntryText(entry);
  const navigate = useNavigate();

  const handleClick = React.useCallback(() => {
    navigate(`${linkPrefix}${entry.id}`);
  }, [navigate, linkPrefix, entry]);

  return (
    <CardContainer onClick={handleClick}>
      <BackgroundImage className="image" image={imageMedium} />
      <GlassOverlay className="glass-overlay" />
      <TypeBadge className="type-badge" bgColor={getContentTypeColor(type)}>{getContentTypeLabel(type)}</TypeBadge>
      <Content>
        <Title>{title}</Title>
      </Content>
    </CardContainer>
  );
});
